import { isArray, join, map, has, isEqual } from "lodash";

import { fullDisplayName } from "src/util/personName";

import { TAG_ATTRIBUTE_TYPES, TAG_ATTRIBUTE_ACTION } from "src/consts/tags";

export type CachedLookups = {
  getCachedGroup?: (id: unknown) => { name?: string } | null | undefined;
  getCachedPerson?: (id: unknown) => unknown;
  getCachedTag?: (id: unknown) => { displayValue?: string } | null | undefined;
};

export const buildDisplayValue = ({
  attrTypeValues,
  attributes,
  separator,
  getCachedGroup,
  getCachedPerson,
  getCachedTag,
  entityId,
}: {
  attrTypeValues: unknown[];
  attributes?: Record<string, unknown>;
  separator?: string;
  entityId?: string;
} & CachedLookups) => {
  return join(
    map(attrTypeValues, (a) =>
      resolveEditorAttrDisplayPart(a, {
        ...(attributes !== undefined ? { attributes } : {}),
        ...(separator !== undefined ? { separator } : {}),
        ...(getCachedGroup !== undefined ? { getCachedGroup } : {}),
        ...(getCachedPerson !== undefined ? { getCachedPerson } : {}),
        ...(getCachedTag !== undefined ? { getCachedTag } : {}),
        ...(entityId !== undefined ? { entityId } : {}),
      })
    ),
    separator
  );
};

export const resolveEditorRelationLabel = (
  type,
  value,
  {
    separator,
    getCachedGroup,
    getCachedPerson,
    getCachedTag,
  }: {
    separator?: string;
    getCachedGroup?: (id: unknown) => { name?: string } | null | undefined;
    getCachedPerson?: (id: unknown) => unknown;
    getCachedTag?: (
      id: unknown
    ) => { displayValue?: string } | null | undefined;
  }
) => {
  if (type === TAG_ATTRIBUTE_TYPES.GROUPTYPE && value) {
    return getCachedGroup?.(value)?.name;
  }
  if (type === TAG_ATTRIBUTE_TYPES.PERSON && value) {
    const person = getCachedPerson?.(value);
    return person
      ? `${fullDisplayName(person)}${separator}${value}`
      : undefined;
  }
  if (type === TAG_ATTRIBUTE_TYPES.TAGTYPE && value) {
    return getCachedTag?.(value)?.displayValue;
  }
  return undefined;
};

export const resolveEditorAttrDisplayPart = (
  attr,
  {
    attributes,
    separator,
    getCachedGroup,
    getCachedPerson,
    getCachedTag,
    entityId,
  }: {
    attributes?: Record<string, unknown>;
    separator?: string;
    getCachedGroup?: (id: unknown) => { name?: string } | null | undefined;
    getCachedPerson?: (id: unknown) => unknown;
    getCachedTag?: (
      id: unknown
    ) => { displayValue?: string } | null | undefined;
    entityId?: string;
  }
) => {
  const { id, label, defaultValue, type } = attr;
  const value = has(attributes, id) ? attributes[id] : defaultValue;
  const related = resolveEditorRelationLabel(type, value, {
    ...(separator !== undefined ? { separator } : {}),
    ...(getCachedGroup !== undefined ? { getCachedGroup } : {}),
    ...(getCachedPerson !== undefined ? { getCachedPerson } : {}),
    ...(getCachedTag !== undefined ? { getCachedTag } : {}),
  });
  if (related) {
    return related;
  }
  if (type === TAG_ATTRIBUTE_TYPES.ENTITY_ID && entityId) {
    return value || entityId;
  }
  return value || `{${label}}`;
};

export const getLabel = (label, isReadOnly = false) => {
  let labelStr = label;

  if (isArray(label)) {
    labelStr = join(label, ", ");
  }

  return `${labelStr}${isReadOnly ? " - readonly" : ""}`;
};

const isReadonlyAttr = (type) => {
  return type.action === TAG_ATTRIBUTE_ACTION.READONLY;
};

export const filterTagAttributeTypes = (
  values: Array<Record<string, unknown>> = [],
  others: Array<Record<string, unknown>> = [],
  entityTagAttrTypes: Array<Record<string, unknown>> = []
) => {
  const combined = [...values, ...others];
  return [
    combined.filter(isReadonlyAttr),
    combined.filter((t) => !isReadonlyAttr(t)),
    entityTagAttrTypes.map((t) => ({ ...t, isEntityTagAttr: true })),
  ];
};

/** Matches Tag attributes column editability (display value + shared fields). */
export const hasEditableTagLevelFields = ({
  includeTagLevelAttributes,
  isDisplayValueEditable,
  editableAttrTypes,
}: {
  includeTagLevelAttributes: boolean;
  isDisplayValueEditable: boolean;
  editableAttrTypes: readonly unknown[];
}) =>
  Boolean(includeTagLevelAttributes) &&
  (isDisplayValueEditable || editableAttrTypes.length > 0);

export const isAttributesChanged = (origin, updated?: unknown) => {
  return !isEqual(origin ?? {}, updated ?? {});
};

/** Entity-tag association id (not shared Tag id) for updateEntityTag mutations. */
export const resolveEntityTagAssociationId = (entityTag?: {
  id?: string;
  tagId?: string;
  tag?: { id?: string };
}) => {
  if (!entityTag?.id) {
    return undefined;
  }
  const tagId = entityTag.tagId ?? entityTag.tag?.id;
  if (tagId && entityTag.id === tagId) {
    return undefined;
  }
  return entityTag.id;
};
